import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useAuth0 } from '../auth/useAuth0';
import { useBusinessContext } from './BusinessContext';
import { useNavigation } from './NavigationContext';
import { MENU } from '../components/util/Utils';
import {
    getMyPlan, getPlanCatalog, requiredPlanFor,
    MyPlan, PlanView, PlanFeatureKey, PlanKey,
} from '../service/plan/plan-service';
import PlanUpsellModal from '../components/plan/PlanUpsellModal';

interface PlanContextType {
    /** The selected business's plan; null while loading / unavailable. */
    plan: MyPlan | null;
    catalog: PlanView[] | null;
    loading: boolean;
    /** Is the feature included in the plan? TRUE while loading (no crown flash —
     *  the backend enforces every operation anyway). */
    hasFeature: (feature: PlanFeatureKey) => boolean;
    /** Cheapest plan that unlocks a feature (upsell copy). */
    requiredPlan: (feature: PlanFeatureKey) => PlanKey;
    /** Open the "incluído no plano X" screen for a locked feature. */
    openUpsell: (feature: PlanFeatureKey) => void;
    /** True only for a business admin (backend-derived). Gates the Plano tab
     *  and every upgrade path; the backend enforces it too. */
    canManagePlan: boolean;
    /** Navigate to Settings → Plano. */
    goToPlanPage: () => void;
    /** Settings reads + clears this to open on the Plano tab. */
    consumeSettingsTabRequest: () => string | null;
    reload: () => void;
}

const PlanContext = createContext<PlanContextType | undefined>(undefined);

export const PlanProvider = ({ children }: { children: ReactNode }) => {
    const { getAccessTokenSilently, isAuthenticated } = useAuth0();
    const { selectedBusiness } = useBusinessContext();
    const { setActiveMenu } = useNavigation();
    const businessId = selectedBusiness?.id;

    const [plan, setPlan] = useState<MyPlan | null>(null);
    const [catalog, setCatalog] = useState<PlanView[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [upsell, setUpsell] = useState<PlanFeatureKey | null>(null);
    const [tabRequest, setTabRequest] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!isAuthenticated || !businessId) {
            setPlan(null);
            return;
        }
        setLoading(true);
        try {
            const token = await getAccessTokenSilently();
            const [p, c] = await Promise.all([
                getMyPlan(businessId, token),
                catalog ? Promise.resolve(catalog) : getPlanCatalog(token).catch(() => null),
            ]);
            setPlan(p);
            if (c) setCatalog(c);
        } catch {
            // Unavailable → no crowns (fail-open for the UI only; backend gates).
            setPlan(null);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, businessId, getAccessTokenSilently]);

    useEffect(() => { void load(); }, [load]);

    const hasFeature = useCallback(
        (feature: PlanFeatureKey) => (plan ? plan.features.includes(feature) : true),
        [plan],
    );
    const requiredPlan = useCallback(
        (feature: PlanFeatureKey) => requiredPlanFor(feature, catalog),
        [catalog],
    );
    const goToPlanPage = useCallback(() => {
        setUpsell(null);
        setTabRequest('Plan');
        setActiveMenu(MENU.SETTINGS);
    }, [setActiveMenu]);
    const consumeSettingsTabRequest = useCallback(() => {
        const r = tabRequest;
        if (r) setTabRequest(null);
        return r;
    }, [tabRequest]);

    const canManagePlan = !!plan?.canManagePlan;

    const value = useMemo<PlanContextType>(() => ({
        plan, catalog, loading, hasFeature, requiredPlan, canManagePlan,
        openUpsell: (f) => setUpsell(f),
        goToPlanPage, consumeSettingsTabRequest, reload: load,
    }), [plan, catalog, loading, hasFeature, requiredPlan, canManagePlan, goToPlanPage, consumeSettingsTabRequest, load]);

    return (
        <PlanContext.Provider value={value}>
            {children}
            {upsell && (
                <PlanUpsellModal
                    feature={upsell}
                    requiredPlan={requiredPlan(upsell)}
                    currentPlan={plan?.plan ?? null}
                    canManagePlan={canManagePlan}
                    onClose={() => setUpsell(null)}
                    onSeePlans={goToPlanPage}
                />
            )}
        </PlanContext.Provider>
    );
};

export const usePlan = (): PlanContextType => {
    const ctx = useContext(PlanContext);
    if (!ctx) throw new Error('usePlan must be used within PlanProvider');
    return ctx;
};
